<script lang="ts">
import type { PopoverTriggerProps } from 'reka-ui'
import type { HTMLAttributes, VNode } from 'vue'
import type { SelectTriggerSize } from '#ui-kit/ui/select'

export type ComboboxTriggerProps = PopoverTriggerProps & {
  size?: SelectTriggerSize
  error?: boolean
  placeholder?: string
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import { reactiveOmit } from '@vueuse/core'
import { PopoverTrigger, useForwardProps } from 'reka-ui'
import { Comment, Text } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { selectTriggerVariants } from '#ui-kit/ui/select'

const props = defineProps<ComboboxTriggerProps>()
const slots = defineSlots<{
  default?: () => VNode[]
  icon?: () => VNode[]
}>()

const delegated = reactiveOmit(props, 'class', 'size', 'error', 'placeholder')
const forwarded = useForwardProps(delegated)

function hasContent(): boolean {
  const nodes = slots.default?.() ?? []

  return nodes.some((node) => {
    if (node.type === Comment) {
      return false
    }
    if (node.type === Text && String(node.children ?? '').trim() === '') {
      return false
    }
    return true
  })
}
</script>

<template>
  <PopoverTrigger
    v-bind="forwarded"
    :aria-invalid="!!props.error"
    :class="cn(selectTriggerVariants({ size: props.size, error: props.error }), props.class)"
  >
    <slot v-if="hasContent()" />
    <span v-else class="text-placeholder text-sm">{{ placeholder }}</span>
    <slot name="icon">
      <ChevronDownIcon class="opacity-50" />
    </slot>
  </PopoverTrigger>
</template>
